import React from "react";
import { Link } from "react-router-dom";
import { GiftIcon, PhoneIcon, EmailIcon } from "../Icons";
import {
  PersonsContainer,
  PersonInfo,
  PersonAvatar,
  PersonAvatarImage,
  PersonDetails,
  PersonName,
  PersonRole,
  PersonExtraInfo,
  PersonExtraItem,
  PersonExtraText,
  PersonsInfoVariant,
} from "./PersonsInfo.styled";

interface Person {
  name: string;
  role: string;
  initials?: string;
  avatarUrl?: string;
  customIcon?: React.ReactNode;
  iconBackgroundColor?: string;
  linkTo?: string;
  dateOfBirth?: string;
  phone?: string;
  email?: string;
}

interface PersonsInfoProps {
  persons: Person[];
  avatarSize?: number;
  size?: "default" | "large";
  margin?: string;
  variant?: PersonsInfoVariant;
}

interface PersonItemProps {
  person: Person;
  avatarStyles: React.CSSProperties;
  nameStyles: React.CSSProperties;
  roleStyles: React.CSSProperties;
  variant: PersonsInfoVariant;
}

const PersonItem: React.FC<PersonItemProps> = ({
  person,
  avatarStyles,
  nameStyles,
  roleStyles,
  variant,
}) => {
  const avatarUrl = person.avatarUrl;
  const customIcon = person.customIcon;
  const iconBackgroundColor = person.iconBackgroundColor;

  const getInitials = (name: string, providedInitials?: string) => {
    if (providedInitials) return providedInitials;
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  return (
    <PersonInfo>
      {customIcon ? (
        <PersonAvatar
          style={{
            ...avatarStyles,
            backgroundColor: iconBackgroundColor || "#E8F4F8",
          }}
        >
          {customIcon}
        </PersonAvatar>
      ) : avatarUrl ? (
        <PersonAvatarImage
          src={avatarUrl}
          alt={person.name}
          style={{ width: avatarStyles.width, height: avatarStyles.height }}
        />
      ) : (
        <PersonAvatar style={avatarStyles}>
          {getInitials(person.name, person.initials)}
        </PersonAvatar>
      )}
      <PersonDetails>
        <PersonRole appearance={variant} style={roleStyles}>
          {person.role}
        </PersonRole>
        {person.linkTo ? (
          <Link
            to={person.linkTo}
            style={{
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <PersonName
              appearance={variant}
              style={{
                ...nameStyles,
                color: "#0E426D",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              {person.name}
            </PersonName>
          </Link>
        ) : (
          <PersonName appearance={variant} style={nameStyles}>
            {person.name}
          </PersonName>
        )}
      </PersonDetails>
      {(person.dateOfBirth || person.phone || person.email) && (
        <PersonExtraInfo>
          {person.dateOfBirth && (
            <PersonExtraItem>
              <GiftIcon />
              <PersonExtraText>{person.dateOfBirth}</PersonExtraText>
            </PersonExtraItem>
          )}
          {person.phone && (
            <PersonExtraItem>
              <PhoneIcon />
              <PersonExtraText>{person.phone}</PersonExtraText>
            </PersonExtraItem>
          )}
          {person.email && (
            <PersonExtraItem>
              <EmailIcon />
              <PersonExtraText>{person.email}</PersonExtraText>
            </PersonExtraItem>
          )}
        </PersonExtraInfo>
      )}
    </PersonInfo>
  );
};

const PersonsInfo: React.FC<PersonsInfoProps> = ({
  persons,
  avatarSize = 48,
  size = "default",
  margin,
  variant = "default",
}) => {
  const avatarStyles = {
    width: `${avatarSize}px`,
    height: `${avatarSize}px`,
    fontSize: `${Math.round(avatarSize * 0.375)}px`,
  };

  const nameStyles = size === "large" ? { fontSize: "20px" } : {};
  const roleStyles = size === "large" ? { fontSize: "16px" } : {};
  const containerStyles = margin !== undefined ? { margin } : {};

  return (
    <PersonsContainer appearance={variant} style={containerStyles}>
      {persons.map((person, index) => (
        <PersonItem
          key={index}
          person={person}
          avatarStyles={avatarStyles}
          nameStyles={nameStyles}
          roleStyles={roleStyles}
          variant={variant}
        />
      ))}
    </PersonsContainer>
  );
};

export default PersonsInfo;
